import React, { useCallback, useState } from 'react';
import {
  refreshForAppUpdate,
  subscribeToServiceWorkerUpdate,
} from '../../serviceWorkerRegistration';
import { clearGamesBrowseCaches } from '../games/browse/gamesApi';
import UpdateAvailableBanner from './UpdateAvailableBanner';
import { notifyCatalogRefresh } from './catalogRefresh';
import { useVersionCheck } from './useVersionCheck';

const AUTO_REFRESH_PATHS = new Set(['/', '/login', '/login-success']);
const AUTO_REFRESH_USED_KEY = 'endchess-deploy-auto-refresh-used';

const isAutoRefreshPath = (pathname: string): boolean =>
  AUTO_REFRESH_PATHS.has(pathname);

const clearAutoRefreshUsed = (): void => {
  try {
    sessionStorage.removeItem(AUTO_REFRESH_USED_KEY);
  } catch {
    // Ignore storage errors (e.g. iOS private mode).
  }
};

const DeployFreshness = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(
    null,
  );

  React.useEffect(() => subscribeToServiceWorkerUpdate(setSwRegistration), []);

  const handleMismatch = useCallback(() => {
    const pathname = window.location.pathname;
    if (isAutoRefreshPath(pathname)) {
      try {
        if (sessionStorage.getItem(AUTO_REFRESH_USED_KEY) === '1') {
          setShowBanner(true);
          return;
        }

        sessionStorage.setItem(AUTO_REFRESH_USED_KEY, '1');
      } catch {
        setShowBanner(true);
        return;
      }

      void refreshForAppUpdate(swRegistration);
      return;
    }

    setShowBanner(true);
  }, [swRegistration]);

  useVersionCheck({
    onClientMismatch: handleMismatch,
    onClientMatch: () => {
      clearAutoRefreshUsed();
      setShowBanner(false);
    },
    onCatalogChange: ({ courses, games }) => {
      if (games) {
        clearGamesBrowseCaches();
      }

      if (courses || games) {
        notifyCatalogRefresh({ courses, games });
      }
    },
  });

  const handleUpdate = () => {
    setShowBanner(false);
    void refreshForAppUpdate(swRegistration);
  };

  return <UpdateAvailableBanner open={showBanner} onUpdate={handleUpdate} />;
};

export default DeployFreshness;
